import * as parse from 'date-fns/parse';
import {Observable} from 'rxjs';
import {publishReplay, refCount} from 'rxjs/operators';
import {CandidateStatus, InterviewStatus, SampleType, TrackerCandidateStatus} from './model';

// RxJS operators
export const cache = () => <T>(source: Observable<T>) => source.pipe(publishReplay(1), refCount());

const entityMap: {[key: string]: string} = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;',
  '/': '&#x2F;'
};

export const KEY_ENTER = 13;
export const KEY_ESC = 27;
export const KEY_LEFT = 37;
export const KEY_RIGHT = 39;

/**
 * Gets the display type for a submittal sample piece.
 *
 * @param sample the submittal sample piece
 * @returns {string} the display type
 */
export function getPortfolioSampleType(sample: Talent.Sample): SampleType {
  if (sample.mimeType.indexOf('image/') === 0) {
    return SampleType.image;
  } else if (sample.mimeType === 'application/pdf' && sample.thumbnailUrl) {
    return SampleType.pdf;
  } else {
    return SampleType.file;
  }
}

/**
 * Gets the appropriate translation key for the specified candidate status ID.
 *
 * @param id the candidate status ID
 */
export function candidateStatusForId(id: number): CandidateStatus {
  switch (id) {
    case 10: // Talent Expressed Interest
    case 20: // Contacted Talent Waiting for Response
    case 30: // Approved By Client Svc Mgr
    case 40: // Approved By Account Director
    case 50: // Submitted To Client
    case 140: // Removed from Order
    case 180: // Contacted Talent for More Info
    case 310: // Gather - Notification Sent
    case 320: // Gather - Interested
      return CandidateStatus.new;
    case 340: // Reviewed by Client
      return CandidateStatus.reviewed;
    case 60: // Client Interview Scheduled
    case 70: // Candidate Feedback Received
    case 80: // Client Feedback Received
    case 150: // Client Interview Complete
    case 190: // 2nd Client Interview Scheduled
    case 200: // 2nd Client Interview Complete
    case 210: // 3rd Client Interview Scheduled
    case 220: // 3rd Client Interview Complete
    case 230: // Client Interview Requested
    case 240: // Client Phone Screen
    case 241: // Client Offer In-Progress
    case 242: // Client Offer Extended
      return CandidateStatus.interview;
    case 90: // Rejected By Client Svc Mgr
    case 100: // Rejected By Account Director
    case 110: // Rejected By Candidate
    case 330: // Gather - Not Interested
      return CandidateStatus.withdrawn;
    case 120: // Rejected By Client
      return CandidateStatus.rejected;
    case 160: // Rejected - Order Filled
      return CandidateStatus.filled;
    case 170: // Rejected - Order Closed
      return CandidateStatus.closed;
    case 130: // Accepted For Order
    case 243: // Client Offer Accepted
      return CandidateStatus.hired;
    default:
      return CandidateStatus.unknown;
  }
}

 export function checkCandidateStatusForGather(id: number): boolean {
  switch (id) {
    case 10: // Talent Applied Online
    case 20: // Contacted Talent Waiting for Response
    case 30: // Approved By Client Svc Mgr
    case 40: // Approved By Account Director
    case 310: // Gather - Notification Sent
    case 342: // Gather Sent (both Email & Text)
    case 343: // Gather Sent (Text)
    case 344: // Gather Sent (Email)
    case 345: // Gather Sent (Custom Email)
    case 346: // Gather Sent (Text) - Interested
    case 347: // Gather Sent (Email) - Interested
    case 348: // Gather Sent (Custom Email) - Interested
    case 349: // Gather Sent (Text) - Not Interested
    case 350: // Gather Sent (Email) - Not Interested
    case 351: // Gather Sent (Custom Email) - Not Interested
    case 352: // Gather Sending (Text)
    case 353: // Gather Scheduled (Text)
    case 354: // Gather Failed (Text)
      return true;
    default:
      return false;
  }
}

export function trackerCandidateStatusForId(id: number): TrackerCandidateStatus {
  switch (id) {
    case 10: // Talent Expressed Interest
    case 20: // Contacted Talent Waiting for Response
    case 30: // Approved By Client Svc Mgr
    case 40: // Approved By Account Director
    case 50: // Submitted To Client
    case 180: // Contacted Talent for More Info
    case 231: // CoreFlex Shortlisted
      return TrackerCandidateStatus.new;
    case 340: // Reviewed By Client
      return TrackerCandidateStatus.reviewed;
    case 60: // Client Interview Scheduled
    case 70: // Candidate Feedback Received
    case 80: // Client Feedback Received
    case 150: // Client Interview Complete
    case 190: // 2nd Client Interview Scheduled
    case 200: // 2nd Client Interview Complete
    case 210: // 3rd Client Interview Scheduled
    case 220: // 3rd Client Interview Complete
    case 230: // Client Interview Requested
    case 240: // Client Phone Screen
      return TrackerCandidateStatus.interviewing;
    case 130: // Accepted For Order
    case 241: // Client Offer In-Progress
    case 242: // Client Offer Extended
    case 243: // Client Offer Accepted
      return TrackerCandidateStatus.offerExtended;
    case 90: // Rejected By Client Svc Mgr
    case 100: // Rejected By Account Director
    case 110:	// Rejected By Candidate
    case 120: // Rejected By Client
    case 140: // Removed from Order
    case 160: // Rejected - Order Filled
    case 170: // Rejected - Order Closed
    case 330: // Gather - Not Interested
      return TrackerCandidateStatus.notInterested;
    default:
      return TrackerCandidateStatus.unknown;
  }
}

export function interviewStatusForStatusId(id: number): InterviewStatus {
  switch (id) {
    case 230: // Client Interview Requested
      return InterviewStatus.requested;
    case 60: // Client Interview Scheduled
    case 190: // 2nd Interview Scheduled
    case 210: // 3rd Interview Scheduled
      return InterviewStatus.scheduled;
    case 70: // Candidate Feedback Received
    case 80: // Client Feedback Received
    case 150: // Client Interview Complete
    case 200: // 2nd Client Interview Complete
    case 220: // 3rd Client Interview Complete
      return InterviewStatus.complete;
    default:
      return InterviewStatus.unknown;
  }
}

export function tryParseJson(str: string): any {
  try {
    return JSON.parse(str);
  } catch (e) {
    return str;
  }
}

export function normalizeWhitespace(str: string): string {
  if (!str) return str;
  return str.replace(/[\s\uFEFF\xA0]/g, ' ');
}

export function escapeRegExp(str: string): string {
  if (!str) return str;
  return str.replace(/[\-\[\]\/{}()*+?.\\\^$|]/g, '\\$&');
}

export function escapeHtml(str: string): string {
  if (!str) return str;
  return str.replace(/[&<>"']/g, (s: string) => entityMap[s]);
}

export function independentAssessmentsUrl(brand: Brand) {
  return 'https://aquent.com/how-different/independent-assessments';
}

const CAMEL_CASE_REGEXP = /([A-Z])/g;
const DASH_CASE_REGEXP = /-([a-z])/g;

export function camelCaseToDashCase(input: string): string {
  return input.replace(CAMEL_CASE_REGEXP, (...m: string[]) => '-' + m[1].toLowerCase());
}

export function dashCaseToCamelCase(input: string): string {
  return input.replace(DASH_CASE_REGEXP, (...m: string[]) => m[1].toUpperCase());
}

/**
 * Workaround for "0" dates in the database
 *
 * @type {Date}
 */
export const MIN_START_DATE = new Date(1);

export function normalizePhone(str: string): string {
  return str ? str.replace(/.?(\d{3})(?:.{1,2})?(\d{3}).?(\d{4})/, '$1 $2 $3') : null;
}

export function parseDate(val: string | number | null): Date | null {
  return val ? (typeof val === 'string' ? parse(val) : new Date(val)) : null;
}

export function closest(el: HTMLElement, predicate: (el: HTMLElement) => boolean): HTMLElement | null {
  do if (predicate(el)) return el;
  while (el = el && el.parentNode as HTMLElement);
  return null;
}

export function blobToString(data: Blob): Promise<string> {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result as string);
    reader.onerror = err => rej(err);
    reader.readAsText(data);
  });
}

/**
 * Try to determine whether the text contains HTML elements.
 * If not, try to convert whitespace to be HTML-friendly.
 *
 * @param {string} text the text to convert
 * @returns {string} the HTML-ified text
 */
export function toHtml(text: string) {
  if (!text || text.match(/<(?:br|p)\s*\/?>/)) {
    return text;
  }
  return text.replace(/ {2}/g, '&nbsp;').replace(/\n/g, '<br>');
}

/**
 * Gets the effective z-index of an element.
 *
 * @param {Element} el the element
 * @returns {number} the effective z-index
 */
export function getEffectiveZIndex(el: Element): number {
  let zIndex = 0, style: CSSStyleDeclaration;
  while (el instanceof Element) {
    if ((style = getComputedStyle(el))) {
      const elZIndex = parseInt(style.zIndex, 10);
      if (elZIndex > zIndex) {
        zIndex = elZIndex;
      }
    }
    el = el.parentNode as Element;
  }
  return zIndex;
}

/**
 * Gets the effective positioning of an element.
 *
 * @param {Element} el the element
 * @returns {typeof CSSStyleDeclaration.prototype.position} static, absolute or fixed
 */
export function getEffectivePosition(el: Element): typeof CSSStyleDeclaration.prototype.position {
  let position = 'static', style: CSSStyleDeclaration;
  while (el instanceof Element) {
    if ((style = getComputedStyle(el))) {
      if (style.position === 'fixed') return 'fixed';
      if (style.position === 'sticky') return 'sticky';
      if (style.position === 'absolute') position = 'absolute';
      if (style.position === 'relative' && position === 'static') position = 'relative';
    }
    el = el.parentElement;
  }
  return position;
}

export const MONTHS_IN_QUARTER = 3;
export const MONTHS_IN_YEAR = 12;

/**
 * Create a date in the previous quarter from a given date.
 *
 * @param {Date} date the reference date
 * @returns {Date} A date in the previous quarter relative to the reference date
 */
export function getDateInPreviousQuarter(date: Date): Date {
  const previousQuarterDate = new Date(date);
  previousQuarterDate.setFullYear(getPreviousQuarterYear(date), getPreviousQuarterMonth(date), 1);

  return previousQuarterDate;
}

function getPreviousQuarterYear(date: Date): number {
  return date.getMonth() < MONTHS_IN_QUARTER ? date.getFullYear() - 1 : date.getFullYear();
}

function getPreviousQuarterMonth(date: Date): number {
  const threeQuarters = 3 * MONTHS_IN_QUARTER;

  return (date.getMonth() + threeQuarters) % MONTHS_IN_YEAR;
}

